/**
 * Executive Command Center — full dashboard UI.
 * Dark-themed executive intelligence platform powered by S3 snapshot data.
 * Uses Eliza Forge design system components with native dark mode theming.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import Layout from '../../components/layout/Layout';
import {
  Page,
  PageHeader,
  PageBody,
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
  Panel,
  PanelHeader,
  PanelTitle,
  PanelBody,
  Badge,
  MetricCard,
  Button,
  Input,
  Textarea,
  Spinner,
  Alert,
  Progress,
  Chip,
} from '../../components/ui';
import { cn } from '../../shared/lib/cn';
import { AXIOS_INSTANCE } from '../../services/api-client';

// ── Types ────────────────────────────────────────────────────────────────────

interface NoiWaterfallBar {
  label: string;
  value: string;
  display_value: string;
  type: 'base' | 'positive' | 'negative' | 'total';
}

interface NoiContributor {
  asset_id: string;
  name: string;
  market: string;
  occupancy_pct: string;
  noi_prior: string;
  noi_current: string;
  contribution: string;
  direction: string;
  flag: string;
}

interface NoiDriver {
  driver: string;
  impact: string;
  amount: string;
  note: string;
}

interface DealUrgency {
  deal_id: string;
  title: string;
  tenant: string;
  market: string;
  sf: string;
  expiry: string;
  noi_at_risk: string;
  priority: string;
  probability: string;
  next_step: string;
  days_to_expiry: number;
}

interface MarketSignal {
  market_id: string;
  name: string;
  signal: string;
  score: number;
  vacancy_pct: string;
  net_absorption: string;
  rent_growth_yoy: string;
  trend: string;
  note: string;
}

interface RecommendedAction {
  id: string;
  title: string;
  rationale: string;
  confidence: string;
  urgency: string;
  agent: string;
}

interface TrustPanel {
  id: string;
  label: string;
  last_refreshed_at: string;
}

interface SnapshotRaw {
  portfolio?: { name?: string; total_assets?: number; total_sqft?: string };
  noi?: {
    currency?: string;
    portfolio_total?: string;
    prior_forecast?: string;
    variance_vs_prior_forecast?: string;
    variance_pct?: string;
    wow_change?: string;
    portfolio_occupancy_pct?: string;
    avg_rent_psf?: string;
    commentary?: string;
  };
  noi_waterfall?: NoiWaterfallBar[];
  noi_contributors?: NoiContributor[];
  noi_drivers?: NoiDriver[];
  deal_tenant_urgency?: DealUrgency[];
  market_pulse?: MarketSignal[];
  recommended_actions?: RecommendedAction[];
  trust?: { panels?: TrustPanel[] };
}

interface SnapshotResponse {
  schema_version: string;
  generated_at?: string | null;
  week_id: string;
  raw: SnapshotRaw;
  storage: Record<string, unknown>;
}

interface ActionRow {
  id: number;
  title: string;
  status: string;
  owner_email?: string | null;
  due_date?: string | null;
  updated_at: string;
}

// ── Helpers ──────────────────────────────────────────────────────────────────

const fmtM = (val: string | number | undefined, showSign = false): string => {
  if (val === undefined || val === '') return '—';
  const n = typeof val === 'string' ? parseFloat(val) : val;
  if (isNaN(n)) return String(val);
  const abs = Math.abs(n);
  const sign = showSign ? (n < 0 ? '-' : n > 0 ? '+' : '') : n < 0 ? '-' : '';
  if (abs >= 1_000_000) return `${sign}$${(abs / 1_000_000).toFixed(1)}M`;
  if (abs >= 1_000) return `${sign}$${(abs / 1_000).toFixed(0)}K`;
  return `${sign}$${abs.toFixed(0)}`;
};

type BV = 'success' | 'warning' | 'danger' | 'info' | 'secondary';

const signalBadgeVariant = (sig: string): BV => {
  switch (sig?.toUpperCase()) {
    case 'STRONG': return 'success';
    case 'NEUTRAL': return 'warning';
    case 'CAUTION': return 'warning';
    case 'AVOID': return 'danger';
    default: return 'secondary';
  }
};

const priorityBadgeVariant = (p: string): BV => {
  switch (p?.toLowerCase()) {
    case 'critical': return 'danger';
    case 'high': return 'warning';
    case 'medium': return 'warning';
    default: return 'secondary';
  }
};

const flagBadgeVariant = (f: string): BV => {
  switch (f?.toUpperCase()) {
    case 'STRONG':
    case 'STABLE': return 'success';
    case 'AT RISK':
    case 'DISTRESS': return 'danger';
    case 'WATCH': return 'warning';
    default: return 'secondary';
  }
};

const urgencyBadgeVariant = (u: string): BV => {
  switch (u?.toLowerCase()) {
    case 'immediate': return 'danger';
    case 'this_week': return 'warning';
    case 'this_month': return 'warning';
    default: return 'secondary';
  }
};

const statusBadgeVariant = (s: string): BV => {
  switch (s?.toLowerCase()) {
    case 'open': return 'warning';
    case 'in_progress': return 'info';
    case 'done': return 'success';
    default: return 'secondary';
  }
};

const signalProgressVariant = (sig: string): 'success' | 'warning' | 'default' => {
  switch (sig?.toUpperCase()) {
    case 'STRONG': return 'success';
    case 'NEUTRAL': return 'warning';
    case 'CAUTION': return 'warning';
    default: return 'default';
  }
};

const priorityProgressVariant = (p: string): 'success' | 'warning' | 'default' => {
  switch (p?.toLowerCase()) {
    case 'critical': return 'default';
    case 'high': return 'warning';
    case 'medium': return 'warning';
    default: return 'default';
  }
};

// ── Custom visualisation components (use inline styles for precise rendering) ─

function WaterfallChart({ bars }: { bars: NoiWaterfallBar[] }) {
  if (!bars.length) return null;
  const maxAbs = Math.max(...bars.map((b) => Math.abs(parseFloat(b.value) || 0)));
  return (
    <div className="flex items-end gap-0.5 h-[120px] px-1">
      {bars.map((bar, i) => {
        const val = parseFloat(bar.value) || 0;
        const h = Math.max(4, (Math.abs(val) / maxAbs) * 96);
        const colorClass =
          bar.type === 'positive'
            ? 'bg-emerald-500'
            : bar.type === 'negative'
              ? 'bg-rose-500'
              : bar.type === 'total'
                ? 'bg-amber-500'
                : 'bg-emerald-500';
        return (
          <div key={i} className="flex-1 flex flex-col items-center gap-0.5">
            <div className="flex-1 flex items-end w-full">
              <div
                className={cn('w-full rounded-t-sm min-h-[4px] opacity-85', colorClass)}
                style={{ height: `${h}%` }}
              />
            </div>
            <div className="text-[8.5px] text-gray-500 dark:text-gray-500 text-center leading-tight">
              {bar.label}
            </div>
            <div className="font-mono text-[8px] text-gray-400 dark:text-gray-400">
              {bar.display_value}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function Ticker({ items }: { items: { key: string; val: string; color?: string }[] }) {
  const doubled = [...items, ...items];
  return (
    <div className="overflow-hidden bg-gray-100 dark:bg-dark-surface-2 border-y border-gray-200 dark:border-dark-border/30 py-1">
      <div
        className="flex gap-10 whitespace-nowrap font-mono text-[10px]"
        style={{ animation: 'ecc-scroll 55s linear infinite' }}
      >
        {doubled.map((item, i) => (
          <div key={i} className="flex items-center gap-1.5 text-gray-500 dark:text-gray-400">
            <span className="text-gray-400 dark:text-gray-500">{item.key}</span>
            <span className="font-bold" style={{ color: item.color }}>{item.val}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Workspace Chat types ─────────────────────────────────────────────────────

interface ChatMessage {
  id: number;
  role: 'user' | 'assistant';
  content: string;
  created_at: string;
  retrieved_chunks?: { content: string; document_name: string; similarity: number; metadata?: Record<string, unknown> }[];
}

interface ChatConversation {
  id: number;
  uuid: string;
  domain_id: number;
  title: string;
}

interface WorkspaceOption {
  id: number;
  name: string;
  display_name: string;
}

// ── Embedded workspace chat ──────────────────────────────────────────────────

const SUGGESTED_QUERIES = [
  'Which assets have NOI risk and upcoming lease expirations?',
  'Summarise the top 3 actions needed this week',
  'Which markets show the best opportunity vs risk?',
  'Explain the biggest NOI drivers this week',
];

function WorkspaceRAGChat() {
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const [workspaces, setWorkspaces] = useState<WorkspaceOption[]>([]);
  const [selectedWorkspaceId, setSelectedWorkspaceId] = useState<number | null>(null);
  const [conversation, setConversation] = useState<ChatConversation | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [isLoadingWorkspaces, setIsLoadingWorkspaces] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => { scrollToBottom(); }, [messages, scrollToBottom]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setIsLoadingWorkspaces(true);
      try {
        const { data } = await AXIOS_INSTANCE.get('/v1/chat/workspaces');
        const list: WorkspaceOption[] = (data.workspaces ?? []).map((w: any) => ({
          id: w.id,
          name: w.name,
          display_name: w.display_name || w.name,
        }));
        if (cancelled) return;
        setWorkspaces(list);

        const fasb = list.find(
          (w) => w.name.toLowerCase() === 'fasb' || w.name.toLowerCase().includes('fasb'),
        );
        const regulated = list.find((w) =>
          w.name.toLowerCase().includes('regulated') || w.name.toLowerCase().includes('standard'),
        );
        const autoSelect = fasb ?? regulated ?? (list.length === 1 ? list[0] : null);
        if (autoSelect) setSelectedWorkspaceId(autoSelect.id);
      } catch (e) {
        if (!cancelled) setError('Failed to load workspaces');
      } finally {
        if (!cancelled) setIsLoadingWorkspaces(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const handleNewConversation = useCallback(() => {
    setConversation(null);
    setMessages([]);
    setInputValue('');
    setError(null);
  }, []);

  const handleSend = useCallback(async (directMessage?: string) => {
    const text = (directMessage ?? inputValue).trim();
    if (!text || isSending || !selectedWorkspaceId) return;

    const messageContent = text;
    setInputValue('');
    setIsSending(true);
    setError(null);

    const tempUserMsg: ChatMessage = {
      id: Date.now(),
      role: 'user',
      content: messageContent,
      created_at: new Date().toISOString(),
    };
    setMessages((prev) => [...prev, tempUserMsg]);

    try {
      let activeConv = conversation;

      if (!activeConv) {
        const { data } = await AXIOS_INSTANCE.post(
          `/v1/ragflow/domains/${selectedWorkspaceId}/conversations`,
          { title: messageContent.slice(0, 80) },
        );
        activeConv = data as ChatConversation;
        setConversation(activeConv);
      }

      const { data: chatResp } = await AXIOS_INSTANCE.post(
        `/v1/ragflow/domains/${selectedWorkspaceId}/conversations/${activeConv!.id}/messages`,
        { content: messageContent },
      );

      setMessages((prev) => [
        ...prev.filter((m) => m.id !== tempUserMsg.id),
        {
          id: chatResp.user_message.id,
          role: 'user',
          content: chatResp.user_message.content,
          created_at: chatResp.user_message.created_at,
        },
        {
          id: chatResp.assistant_message.id,
          role: 'assistant',
          content: chatResp.assistant_message.content,
          created_at: chatResp.assistant_message.created_at,
          retrieved_chunks: chatResp.assistant_message.retrieved_chunks,
        },
      ]);
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Failed to send message');
      setMessages((prev) => prev.filter((m) => m.id !== tempUserMsg.id));
    } finally {
      setIsSending(false);
      inputRef.current?.focus();
    }
  }, [inputValue, isSending, selectedWorkspaceId, conversation]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleSend();
      }
    },
    [handleSend],
  );

  if (isLoadingWorkspaces) {
    return (
      <div className="flex items-center justify-center gap-3 py-12">
        <Spinner size="md" />
        <span className="text-sm text-gray-500 dark:text-gray-400">Loading workspaces…</span>
      </div>
    );
  }

  if (workspaces.length === 0) {
    return (
      <Alert variant="warning" title="No workspaces available">
        Create a workspace with documents (e.g. FASB standards) to enable the chat interface.
      </Alert>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      {/* Workspace selector + new conversation */}
      <div className="flex items-center gap-3">
        <div className="flex items-center gap-2 flex-1">
          <label className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 whitespace-nowrap">
            Workspace
          </label>
          <select
            value={selectedWorkspaceId ?? ''}
            onChange={(e) => {
              const id = Number(e.target.value);
              setSelectedWorkspaceId(id || null);
              handleNewConversation();
            }}
            className="bg-white dark:bg-dark-surface border border-gray-200 dark:border-dark-border/50 text-charcoal dark:text-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:border-eliza-red/50"
          >
            <option value="">Select workspace…</option>
            {workspaces.map((w) => (
              <option key={w.id} value={w.id}>{w.display_name}</option>
            ))}
          </select>
        </div>
        {conversation && (
          <Button variant="outline" size="sm" onClick={handleNewConversation}>
            New conversation
          </Button>
        )}
      </div>

      {error && (
        <Alert variant="error" title="Error">{error}</Alert>
      )}

      {/* Chat messages */}
      <Panel className="min-h-[300px]">
        <PanelBody className="p-0">
          <div className="max-h-[480px] overflow-y-auto p-6 space-y-4">
            {messages.length === 0 && !isSending && (
              <div className="text-center py-10">
                <div className="text-3xl mb-3">💬</div>
                <h3 className="text-base font-medium text-gray-500 dark:text-gray-400">
                  Ask a question
                </h3>
                <p className="text-sm text-gray-400 dark:text-gray-500 mt-1">
                  Chat with the {workspaces.find((w) => w.id === selectedWorkspaceId)?.display_name ?? 'workspace'} knowledge base
                </p>
              </div>
            )}
            {messages.map((msg) => (
              <div
                key={msg.id}
                className={cn('flex', msg.role === 'user' ? 'justify-end' : 'justify-start')}
              >
                <div
                  className={cn(
                    'max-w-[80%] rounded-lg px-4 py-3',
                    msg.role === 'user'
                      ? 'bg-eliza-red text-white'
                      : 'bg-gray-50 dark:bg-dark-surface-2 border border-gray-200 dark:border-dark-border/30',
                  )}
                >
                  {msg.role === 'assistant' ? (
                    <div className="prose prose-sm dark:prose-invert max-w-none text-charcoal dark:text-gray-100">
                      <ReactMarkdown remarkPlugins={[remarkGfm]}>{msg.content}</ReactMarkdown>
                    </div>
                  ) : (
                    <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                  )}
                  {msg.retrieved_chunks && msg.retrieved_chunks.length > 0 && (
                    <div className="mt-3 pt-3 border-t border-gray-200 dark:border-dark-border/30">
                      <p className="text-xs text-gray-500 dark:text-gray-400 mb-1.5">
                        Sources ({msg.retrieved_chunks.length})
                      </p>
                      <div className="space-y-1">
                        {msg.retrieved_chunks.slice(0, 5).map((chunk, i) => (
                          <div key={i} className="text-xs text-gray-400 dark:text-gray-500 truncate">
                            • {(chunk.metadata as any)?.filename || chunk.document_name || `Source ${i + 1}`}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}
            {isSending && (
              <div className="flex justify-start">
                <div className="bg-gray-50 dark:bg-dark-surface-2 border border-gray-200 dark:border-dark-border/30 rounded-lg px-4 py-3">
                  <div className="flex items-center gap-2">
                    <Spinner size="sm" />
                    <span className="text-sm text-gray-500 dark:text-gray-400">Thinking…</span>
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>
        </PanelBody>
      </Panel>

      {/* Suggested prompts */}
      {messages.length === 0 && (
        <div>
          <div className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-2">
            Suggested prompts
          </div>
          <div className="flex flex-wrap gap-1.5">
            {SUGGESTED_QUERIES.map((q, i) => (
              <Chip
                key={i}
                showIcon={false}
                onClick={() => handleSend(q)}
              >
                {q}
              </Chip>
            ))}
          </div>
        </div>
      )}

      {/* Input area */}
      <div className="flex gap-2 items-end">
        <Textarea
          ref={inputRef}
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={selectedWorkspaceId ? 'Ask a question…' : 'Select a workspace first…'}
          rows={2}
          disabled={!selectedWorkspaceId}
        />
        <Button
          variant="brand"
          onClick={() => handleSend()}
          disabled={!inputValue.trim() || isSending || !selectedWorkspaceId}
        >
          {isSending ? <Spinner size="sm" variant="white" /> : 'Send'}
        </Button>
      </div>
    </div>
  );
}

// ── Main Page ────────────────────────────────────────────────────────────────

type TabId = 'briefing' | 'deals' | 'markets' | 'query' | 'actions';

export default function ExecutiveCommandCenterPage() {
  const qc = useQueryClient();
  const [weekId, setWeekId] = useState<string>('');
  const [activeTab, setActiveTab] = useState<TabId>('briefing');
  const [newTitle, setNewTitle] = useState('');
  const [confirmedActions, setConfirmedActions] = useState<Set<string>>(new Set());

  const weekParam = weekId.trim() || undefined;

  // ── Queries & Mutations ──────────────────────────────────────────────────

  const snapshotQuery = useQuery({
    queryKey: ['ecc-snapshot', weekParam],
    queryFn: async () => {
      const { data } = await AXIOS_INSTANCE.get<SnapshotResponse>(
        '/v1/executive-command-center/snapshot',
        { params: weekParam ? { week_id: weekParam } : {} },
      );
      return data;
    },
  });

  const weeksQuery = useQuery({
    queryKey: ['ecc-weeks'],
    queryFn: async () => {
      const { data } = await AXIOS_INSTANCE.get<{ week_ids: string[] }>(
        '/v1/executive-command-center/weeks',
      );
      return data.week_ids;
    },
  });

  const actionsQuery = useQuery({
    queryKey: ['ecc-actions'],
    queryFn: async () => {
      const { data } = await AXIOS_INSTANCE.get<{ items: ActionRow[]; total: number }>(
        '/v1/executive-command-center/actions',
      );
      return data;
    },
  });

  const createActionMutation = useMutation({
    mutationFn: async ({ title, sourceType = 'manual' }: { title: string; sourceType?: 'noi' | 'deal' | 'market' | 'cross_agent' | 'manual' }) => {
      await AXIOS_INSTANCE.post('/v1/executive-command-center/actions', {
        title,
        source: { type: sourceType, ref_ids: [] },
      });
    },
    onSuccess: () => {
      setNewTitle('');
      qc.invalidateQueries({ queryKey: ['ecc-actions'] });
    },
  });

  const updateActionMutation = useMutation({
    mutationFn: async ({ id, status }: { id: number; status: string }) => {
      await AXIOS_INSTANCE.patch(`/v1/executive-command-center/actions/${id}`, { status });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['ecc-actions'] });
    },
  });

  const createActionFromRec = (key: string, title: string, sourceType: 'noi' | 'deal' | 'market' | 'cross_agent' | 'manual' = 'manual') => {
    createActionMutation.mutate({ title, sourceType });
    setConfirmedActions((prev) => new Set(prev).add(key));
  };

  // ── Derived Data ─────────────────────────────────────────────────────────

  const raw: SnapshotRaw = snapshotQuery.data?.raw ?? {};
  const noi = raw.noi ?? {};
  const portfolio = raw.portfolio ?? {};
  const waterfall = raw.noi_waterfall ?? [];
  const contributors = raw.noi_contributors ?? [];
  const drivers = raw.noi_drivers ?? [];
  const dealUrgency = raw.deal_tenant_urgency ?? [];
  const marketPulse = raw.market_pulse ?? [];
  const recommendedActions = raw.recommended_actions ?? [];

  const variancePct = parseFloat(noi.variance_pct || '0');
  const criticalDeals = dealUrgency.filter((d) => d.priority === 'critical').length;
  const alertCount =
    criticalDeals +
    marketPulse.filter((m) => m.signal === 'AVOID' || m.signal === 'CAUTION').length;

  const tickerItems = useMemo(() => {
    const items: { key: string; val: string; color?: string }[] = [];
    if (noi.portfolio_total)
      items.push({ key: 'PORTFOLIO NOI', val: fmtM(noi.portfolio_total), color: '#10B981' });
    if (noi.variance_pct)
      items.push({
        key: 'VS FORECAST',
        val: `${variancePct > 0 ? '+' : ''}${noi.variance_pct}%`,
        color: variancePct < 0 ? '#F43F5E' : '#10B981',
      });
    if (noi.portfolio_occupancy_pct)
      items.push({ key: 'OCCUPANCY', val: `${noi.portfolio_occupancy_pct}%`, color: '#F59E0B' });
    items.push({
      key: 'CRITICAL DEALS',
      val: String(criticalDeals),
      color: criticalDeals > 0 ? '#F43F5E' : '#10B981',
    });
    marketPulse.slice(0, 5).forEach((m) => {
      const arrow = m.trend === 'improving' ? '↑' : m.trend === 'stable' ? '→' : '↓';
      const c = m.signal === 'STRONG' ? '#10B981' : m.signal === 'NEUTRAL' ? '#F59E0B' : m.signal === 'CAUTION' ? '#F97316' : '#F43F5E';
      items.push({ key: m.name.toUpperCase(), val: `${m.score} ${arrow}`, color: c });
    });
    return items;
  }, [noi, marketPulse, criticalDeals, variancePct]);

  const weekDesc = snapshotQuery.data
    ? `${snapshotQuery.data.week_id}${portfolio.name ? ` · ${portfolio.name}` : ''}`
    : undefined;

  // ── Render ───────────────────────────────────────────────────────────────

  return (
    <Layout>
      <style>{`
        @keyframes ecc-scroll {
          0%   { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        @keyframes ecc-pulse {
          0%, 100% { opacity: 1; box-shadow: 0 0 0 0 rgba(16,185,129,0.4); }
          50%       { opacity: 0.7; box-shadow: 0 0 0 4px transparent; }
        }
        @keyframes ecc-fadein {
          from { opacity: 0; transform: translateY(6px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>

      <Page layout="full-width" variant="default">
        <PageHeader
          title="Executive Command Center"
          description={weekDesc}
          bordered
          actions={
            <div className="flex items-center gap-3">
              <Badge variant="success" className="gap-1.5">
                <span
                  className="w-1.5 h-1.5 rounded-full bg-emerald-500 dark:bg-emerald-400"
                  style={{ animation: 'ecc-pulse 2s infinite' }}
                />
                LIVE
              </Badge>
              {weeksQuery.data && weeksQuery.data.length > 0 && (
                <select
                  value={weekId}
                  onChange={(e) => setWeekId(e.target.value)}
                  className="bg-white dark:bg-dark-surface border border-gray-200 dark:border-dark-border/50 text-charcoal dark:text-gray-300 rounded-lg px-2.5 py-1.5 text-xs font-mono focus:outline-none focus:border-eliza-red/50 dark:focus:border-eliza-red-light/50"
                >
                  <option value="">Current week</option>
                  {weeksQuery.data.map((w) => (
                    <option key={w} value={w}>{w}</option>
                  ))}
                </select>
              )}
              <Button
                variant="secondary"
                size="sm"
                onClick={() => snapshotQuery.refetch()}
                disabled={snapshotQuery.isFetching}
              >
                {snapshotQuery.isFetching ? '↻ Loading…' : '↻ Refresh'}
              </Button>
            </div>
          }
        />

        <PageBody padded={false}>
          {tickerItems.length > 0 && <Ticker items={tickerItems} />}

          {snapshotQuery.isLoading && (
            <div className="flex items-center justify-center gap-3 py-16">
              <Spinner size="md" />
              <span className="text-xs text-gray-500 dark:text-gray-400 font-mono tracking-wider">Loading snapshot…</span>
            </div>
          )}

          {snapshotQuery.isError && (
            <div className="p-5 mx-5">
              <Alert variant="error" title="Could not load snapshot">
                Ensure tenant object storage is configured and a snapshot is uploaded to the S3 key{' '}
                <code className="font-mono text-[10px] text-sky-600 dark:text-sky-400">
                  executive-command-center/{'<tenant>'}/weekly/{'<week>'}/dashboard_snapshot.json
                </code>
              </Alert>
            </div>
          )}

          {snapshotQuery.data && (
            <div className="px-8 pb-8">
              {/* ─── KPI Row ─── */}
              <div className="grid grid-cols-4 gap-3 my-4">
                <MetricCard
                  title="Portfolio NOI"
                  value={fmtM(noi.portfolio_total)}
                  trend={noi.wow_change === 'down' ? 'down' : 'up'}
                  changeLabel={noi.wow_change === 'down' ? 'Week-over-week decline' : 'Week-over-week increase'}
                  variant="outlined"
                />
                <MetricCard
                  title="vs Prior Forecast"
                  value={fmtM(noi.variance_vs_prior_forecast, true)}
                  change={variancePct}
                  changeLabel="variance"
                  trend={variancePct < 0 ? 'down' : 'up'}
                  variant="outlined"
                />
                <MetricCard
                  title="Portfolio Occupancy"
                  value={`${noi.portfolio_occupancy_pct ?? '—'}%`}
                  changeLabel={noi.avg_rent_psf ? `$${noi.avg_rent_psf} avg rent PSF` : undefined}
                  trend="neutral"
                  variant="outlined"
                />
                <MetricCard
                  title="Active Alerts"
                  value={String(alertCount)}
                  changeLabel={`${criticalDeals} critical deal${criticalDeals !== 1 ? 's' : ''}`}
                  trend={criticalDeals > 0 ? 'down' : 'neutral'}
                  variant="outlined"
                />
              </div>

              {/* ─── Tabs ─── */}
              <Tabs
                defaultValue="briefing"
                value={activeTab}
                onValueChange={(v) => setActiveTab(v as TabId)}
              >
                <TabsList variant="underline">
                  <TabsTrigger value="briefing" variant="underline">
                    Weekly Briefing
                  </TabsTrigger>
                  <TabsTrigger value="deals" variant="underline">
                    Deal Urgency{criticalDeals > 0 ? ` · ${criticalDeals} critical` : ''}
                  </TabsTrigger>
                  <TabsTrigger value="markets" variant="underline">
                    Market Pulse
                  </TabsTrigger>
                  <TabsTrigger value="query" variant="underline">
                    Cross-Agent Query
                  </TabsTrigger>
                  <TabsTrigger value="actions" variant="underline">
                    Actions{actionsQuery.data?.total ? ` (${actionsQuery.data.total})` : ''}
                  </TabsTrigger>
                </TabsList>

                {/* ═══════════════ BRIEFING ═══════════════ */}
                <TabsContent value="briefing">
                  <div className="grid grid-cols-2 gap-4">
                    {/* Left column */}
                    <div className="flex flex-col gap-3">
                      <Panel>
                        <PanelHeader className="flex items-center justify-between">
                          <PanelTitle>NOI Flux Waterfall — vs Prior Forecast</PanelTitle>
                          <Badge variant="success">AI GENERATED</Badge>
                        </PanelHeader>
                        <PanelBody>
                          {waterfall.length > 0 ? (
                            <WaterfallChart bars={waterfall} />
                          ) : (
                            <div className="h-20 flex items-center justify-center text-gray-500 dark:text-gray-400 text-sm">
                              No waterfall data in snapshot
                            </div>
                          )}
                          {noi.commentary && (
                            <div className="bg-gray-50 dark:bg-dark-surface-2 rounded-lg mt-3 p-3 text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
                              <span className="text-emerald-600 dark:text-emerald-400 font-semibold">AI Commentary: </span>
                              {noi.commentary}
                            </div>
                          )}
                        </PanelBody>
                      </Panel>

                      {drivers.length > 0 && (
                        <Panel>
                          <PanelHeader>
                            <PanelTitle>NOI Drivers</PanelTitle>
                          </PanelHeader>
                          <PanelBody className="p-0 py-1">
                            {drivers.map((d, i) => (
                              <div
                                key={i}
                                className={cn(
                                  'flex items-start gap-2.5 px-6 py-2',
                                  i < drivers.length - 1 && 'border-b border-gray-100 dark:border-dark-border/20',
                                )}
                              >
                                <div
                                  className={cn(
                                    'w-2 h-2 rounded-full shrink-0 mt-1.5',
                                    d.impact === 'positive' ? 'bg-emerald-500 dark:bg-emerald-400' : 'bg-rose-500 dark:bg-rose-400',
                                  )}
                                />
                                <div className="flex-1">
                                  <div className="text-sm text-charcoal dark:text-gray-100 font-medium">
                                    <span className="capitalize">{d.driver.replace(/_/g, ' ')}</span>
                                    <span
                                      className={cn(
                                        'ml-2 font-mono text-xs',
                                        d.impact === 'positive' ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400',
                                      )}
                                    >
                                      {fmtM(d.amount, true)}
                                    </span>
                                  </div>
                                  <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{d.note}</div>
                                </div>
                              </div>
                            ))}
                          </PanelBody>
                        </Panel>
                      )}
                    </div>

                    {/* Right column */}
                    <div className="flex flex-col gap-3">
                      <Panel>
                        <PanelHeader className="flex items-center justify-between">
                          <PanelTitle>Recommended Actions</PanelTitle>
                          <Badge variant="warning">{recommendedActions.length} recommendations</Badge>
                        </PanelHeader>
                        <PanelBody className="flex flex-col gap-2">
                          {recommendedActions.map((rec, i) => (
                            <div
                              key={i}
                              className="bg-gray-50 dark:bg-dark-surface-2/50 border border-gray-200 dark:border-dark-border/30 rounded-lg p-3 transition-colors hover:border-gray-300 dark:hover:border-dark-border/50"
                            >
                              <div className="flex justify-between items-start mb-1.5">
                                <div className="text-sm font-semibold text-charcoal dark:text-gray-100 flex-1 mr-2 leading-snug">
                                  {rec.title}
                                </div>
                                <div className="flex gap-1 shrink-0 flex-wrap justify-end">
                                  <Badge variant={urgencyBadgeVariant(rec.urgency)}>
                                    {rec.urgency?.replace(/_/g, ' ').toUpperCase() || 'PENDING'}
                                  </Badge>
                                  <Badge variant="info">{rec.agent || 'AI'}</Badge>
                                </div>
                              </div>
                              <div className="text-xs text-gray-500 dark:text-gray-400 leading-relaxed mb-2">
                                {rec.rationale}
                              </div>
                              <Button
                                variant={confirmedActions.has(`rec-${i}`) ? 'brand' : 'outline'}
                                size="sm"
                                disabled={confirmedActions.has(`rec-${i}`)}
                                onClick={() => createActionFromRec(`rec-${i}`, rec.title, 'noi')}
                              >
                                {confirmedActions.has(`rec-${i}`) ? '✓ Added' : '+ Create action'}
                              </Button>
                            </div>
                          ))}
                          {recommendedActions.length === 0 && (
                            <div className="p-4 text-center text-gray-500 dark:text-gray-400 text-sm">
                              No recommended actions in snapshot
                            </div>
                          )}
                        </PanelBody>
                      </Panel>

                      {contributors.length > 0 && (
                        <Panel>
                          <PanelHeader className="flex items-center justify-between">
                            <PanelTitle>Top / Bottom Assets — NOI Flux</PanelTitle>
                            <Badge variant="danger">
                              {contributors.filter((c) => c.direction === 'negative').length} ALERTS
                            </Badge>
                          </PanelHeader>
                          <PanelBody className="p-0">
                            <div className="overflow-x-auto">
                              <table className="w-full border-collapse">
                                <thead>
                                  <tr>
                                    {['Asset', 'Market', 'Occ%', 'NOI Flux', 'Flag'].map((h) => (
                                      <th key={h} className="px-4 py-2.5 text-left text-xs font-semibold tracking-wider uppercase text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-dark-border/30 whitespace-nowrap bg-gray-50 dark:bg-dark-surface-2/50">
                                        {h}
                                      </th>
                                    ))}
                                  </tr>
                                </thead>
                                <tbody>
                                  {contributors.slice(0, 8).map((c, i) => (
                                    <tr key={i} className="border-b border-gray-100 dark:border-dark-border/20 hover:bg-gray-50 dark:hover:bg-dark-surface-2/30 transition-colors">
                                      <td className="px-4 py-2 text-sm">
                                        <span className="font-semibold text-charcoal dark:text-gray-100">{c.name}</span>
                                      </td>
                                      <td className="px-4 py-2 text-xs text-gray-500 dark:text-gray-400">
                                        {c.market}
                                      </td>
                                      <td
                                        className={cn(
                                          'px-4 py-2 font-mono text-sm',
                                          parseFloat(c.occupancy_pct) >= 85
                                            ? 'text-emerald-600 dark:text-emerald-400'
                                            : parseFloat(c.occupancy_pct) < 60
                                              ? 'text-rose-600 dark:text-rose-400'
                                              : 'text-amber-600 dark:text-amber-400',
                                        )}
                                      >
                                        {c.occupancy_pct}%
                                      </td>
                                      <td
                                        className={cn(
                                          'px-4 py-2 font-mono text-sm',
                                          c.direction === 'positive' ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400',
                                        )}
                                      >
                                        {fmtM(c.contribution, true)}
                                      </td>
                                      <td className="px-4 py-2">
                                        <Badge variant={flagBadgeVariant(c.flag)}>{c.flag}</Badge>
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </PanelBody>
                        </Panel>
                      )}
                    </div>
                  </div>
                </TabsContent>

                {/* ═══════════════ DEALS ═══════════════ */}
                <TabsContent value="deals">
                  <div className="flex flex-col gap-4">
                    <Panel>
                      <PanelHeader className="flex items-center justify-between">
                        <PanelTitle>Deal / Tenant Urgency Watchlist</PanelTitle>
                        <div className="flex gap-1.5">
                          <Badge variant="danger">{criticalDeals} CRITICAL</Badge>
                          <Badge variant="info">{dealUrgency.length} TOTAL</Badge>
                        </div>
                      </PanelHeader>
                      <PanelBody className="p-0">
                        <div className="overflow-x-auto">
                          <table className="w-full border-collapse">
                            <thead>
                              <tr>
                                {['Tenant', 'Market', 'SF', 'Expiry', 'Days', 'NOI at Risk', 'Probability', 'Priority', 'Next Step', ''].map((h) => (
                                  <th key={h} className="px-4 py-2.5 text-left text-xs font-semibold tracking-wider uppercase text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-dark-border/30 whitespace-nowrap bg-gray-50 dark:bg-dark-surface-2/50">
                                    {h}
                                  </th>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              {dealUrgency.map((d, i) => (
                                <tr key={i} className="border-b border-gray-100 dark:border-dark-border/20 hover:bg-gray-50 dark:hover:bg-dark-surface-2/30 transition-colors">
                                  <td className="px-4 py-2 text-sm">
                                    <span className="font-semibold text-charcoal dark:text-gray-100">{d.tenant}</span>
                                  </td>
                                  <td className="px-4 py-2 text-xs text-gray-500 dark:text-gray-400">{d.market}</td>
                                  <td className="px-4 py-2 font-mono text-sm">{parseInt(d.sf).toLocaleString()}</td>
                                  <td
                                    className={cn(
                                      'px-4 py-2 font-mono text-sm',
                                      d.days_to_expiry <= 60
                                        ? 'text-rose-600 dark:text-rose-400'
                                        : d.days_to_expiry <= 120
                                          ? 'text-amber-600 dark:text-amber-400'
                                          : 'text-gray-500 dark:text-gray-400',
                                    )}
                                  >
                                    {d.expiry}
                                  </td>
                                  <td
                                    className={cn(
                                      'px-4 py-2 font-mono text-sm',
                                      d.days_to_expiry <= 60
                                        ? 'text-rose-600 dark:text-rose-400'
                                        : d.days_to_expiry <= 120
                                          ? 'text-amber-600 dark:text-amber-400'
                                          : 'text-gray-500 dark:text-gray-400',
                                    )}
                                  >
                                    {d.days_to_expiry}d
                                  </td>
                                  <td
                                    className={cn(
                                      'px-4 py-2 font-mono text-sm',
                                      parseFloat(d.noi_at_risk) > 0 ? 'text-rose-600 dark:text-rose-400' : 'text-emerald-600 dark:text-emerald-400',
                                    )}
                                  >
                                    {parseFloat(d.noi_at_risk) > 0 ? fmtM(d.noi_at_risk) : '—'}
                                  </td>
                                  <td className="px-4 py-2">
                                    <div className="flex items-center gap-1.5">
                                      <Progress
                                        value={parseFloat(d.probability)}
                                        size="sm"
                                        variant={priorityProgressVariant(d.priority)}
                                        className="w-[52px]"
                                      />
                                      <span className="font-mono text-xs font-bold text-gray-600 dark:text-gray-300">
                                        {d.probability}%
                                      </span>
                                    </div>
                                  </td>
                                  <td className="px-4 py-2">
                                    <Badge variant={priorityBadgeVariant(d.priority)}>
                                      {d.priority.toUpperCase()}
                                    </Badge>
                                  </td>
                                  <td className="px-4 py-2 text-xs text-gray-500 dark:text-gray-400 max-w-[180px]">
                                    {d.next_step}
                                  </td>
                                  <td className="px-4 py-2">
                                    <Button
                                      variant={confirmedActions.has(`deal-${i}`) ? 'brand' : 'outline'}
                                      size="sm"
                                      disabled={confirmedActions.has(`deal-${i}`)}
                                      onClick={() => createActionFromRec(`deal-${i}`, d.title, 'deal')}
                                    >
                                      {confirmedActions.has(`deal-${i}`) ? '✓ Added' : '+ Action'}
                                    </Button>
                                  </td>
                                </tr>
                              ))}
                              {dealUrgency.length === 0 && (
                                <tr>
                                  <td colSpan={10} className="px-6 py-6 text-center text-gray-500 dark:text-gray-400 text-sm">
                                    No deal / tenant urgency data in snapshot
                                  </td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                        </div>
                      </PanelBody>
                    </Panel>
                  </div>
                </TabsContent>

                {/* ═══════════════ MARKETS ═══════════════ */}
                <TabsContent value="markets">
                  <div className="flex flex-col gap-4">
                    <div className="grid grid-cols-4 gap-3">
                      <MetricCard
                        title="STRONG Markets"
                        value={String(marketPulse.filter((m) => m.signal === 'STRONG').length)}
                        changeLabel={marketPulse.filter((m) => m.signal === 'STRONG').map((m) => m.name).join(' · ')}
                        trend="up"
                        variant="outlined"
                      />
                      <MetricCard
                        title="NEUTRAL Markets"
                        value={String(marketPulse.filter((m) => m.signal === 'NEUTRAL').length)}
                        changeLabel="Monitor for inflection"
                        trend="neutral"
                        variant="outlined"
                      />
                      <MetricCard
                        title="CAUTION Markets"
                        value={String(marketPulse.filter((m) => m.signal === 'CAUTION').length)}
                        changeLabel="Reduce exposure"
                        trend="down"
                        variant="outlined"
                      />
                      <MetricCard
                        title="AVOID Markets"
                        value={String(marketPulse.filter((m) => m.signal === 'AVOID').length)}
                        changeLabel="Evaluate disposition"
                        trend="down"
                        variant="outlined"
                      />
                    </div>

                    <Panel>
                      <PanelHeader className="flex items-center justify-between">
                        <PanelTitle>Market Signal Scorecard</PanelTitle>
                        <Badge variant="info">LIVE DATA</Badge>
                      </PanelHeader>
                      <PanelBody className="p-0">
                        <div className="overflow-x-auto">
                          <table className="w-full border-collapse">
                            <thead>
                              <tr>
                                {['Market', 'Signal', 'Score', 'Vacancy %', 'Net Absorption', 'Rent Growth YoY', 'Trend', 'Note', ''].map((h) => (
                                  <th key={h} className="px-4 py-2.5 text-left text-xs font-semibold tracking-wider uppercase text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-dark-border/30 whitespace-nowrap bg-gray-50 dark:bg-dark-surface-2/50">
                                    {h}
                                  </th>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              {marketPulse.map((m, i) => (
                                <tr key={i} className="border-b border-gray-100 dark:border-dark-border/20 hover:bg-gray-50 dark:hover:bg-dark-surface-2/30 transition-colors">
                                  <td className="px-4 py-2 text-sm">
                                    <span className="font-semibold text-charcoal dark:text-gray-100">{m.name}</span>
                                  </td>
                                  <td className="px-4 py-2">
                                    <Badge variant={signalBadgeVariant(m.signal)}>{m.signal}</Badge>
                                  </td>
                                  <td className="px-4 py-2">
                                    <div className="flex items-center gap-1.5">
                                      <Progress
                                        value={m.score}
                                        size="sm"
                                        variant={signalProgressVariant(m.signal)}
                                        className="w-[60px]"
                                      />
                                      <span className="font-mono text-sm font-bold text-gray-600 dark:text-gray-300 min-w-[24px]">
                                        {m.score}
                                      </span>
                                    </div>
                                  </td>
                                  <td className="px-4 py-2 font-mono text-sm">{m.vacancy_pct}%</td>
                                  <td
                                    className={cn(
                                      'px-4 py-2 font-mono text-sm',
                                      m.net_absorption.startsWith('+') ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400',
                                    )}
                                  >
                                    {m.net_absorption}
                                  </td>
                                  <td
                                    className={cn(
                                      'px-4 py-2 font-mono text-sm',
                                      m.rent_growth_yoy.startsWith('+') ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400',
                                    )}
                                  >
                                    {m.rent_growth_yoy}
                                  </td>
                                  <td
                                    className={cn(
                                      'px-4 py-2 text-xs',
                                      m.trend.includes('improv')
                                        ? 'text-emerald-600 dark:text-emerald-400'
                                        : m.trend === 'stable'
                                          ? 'text-amber-600 dark:text-amber-400'
                                          : 'text-rose-600 dark:text-rose-400',
                                    )}
                                  >
                                    {m.trend === 'improving'
                                      ? '↑ Improving'
                                      : m.trend === 'stable'
                                        ? '→ Stable'
                                        : m.trend === 'weakening'
                                          ? '↓ Weakening'
                                          : '↓↓ Structural'}
                                  </td>
                                  <td className="px-4 py-2 text-xs text-gray-500 dark:text-gray-400 max-w-[200px]">
                                    {m.note}
                                  </td>
                                  <td className="px-4 py-2">
                                    <Button
                                      variant={confirmedActions.has(`mkt-${i}`) ? 'brand' : 'outline'}
                                      size="sm"
                                      disabled={confirmedActions.has(`mkt-${i}`)}
                                      onClick={() => createActionFromRec(`mkt-${i}`, `${m.name}: ${m.note}`, 'market')}
                                    >
                                      {confirmedActions.has(`mkt-${i}`) ? '✓ Added' : '+ Action'}
                                    </Button>
                                  </td>
                                </tr>
                              ))}
                              {marketPulse.length === 0 && (
                                <tr>
                                  <td colSpan={9} className="px-6 py-6 text-center text-gray-500 dark:text-gray-400 text-sm">
                                    No market pulse data in snapshot
                                  </td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                        </div>
                      </PanelBody>
                    </Panel>
                  </div>
                </TabsContent>

                {/* ═══════════════ QUERY ═══════════════ */}
                <TabsContent value="query">
                  <WorkspaceRAGChat />
                </TabsContent>

                {/* ═══════════════ ACTIONS ═══════════════ */}
                <TabsContent value="actions">
                  <div className="flex flex-col gap-4">
                    <Panel>
                      <PanelHeader>
                        <PanelTitle>Create Action</PanelTitle>
                      </PanelHeader>
                      <PanelBody>
                        <div className="flex gap-2">
                          <Input
                            value={newTitle}
                            onChange={(e) => setNewTitle(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' && newTitle.trim()) {
                                createActionMutation.mutate({ title: newTitle.trim() });
                              }
                            }}
                            placeholder="Action title…"
                          />
                          <Button
                            variant="brand"
                            onClick={() => newTitle.trim() && createActionMutation.mutate({ title: newTitle.trim() })}
                            disabled={!newTitle.trim() || createActionMutation.isPending}
                          >
                            Create
                          </Button>
                        </div>
                      </PanelBody>
                    </Panel>

                    <Panel>
                      <PanelHeader className="flex items-center justify-between">
                        <PanelTitle>Action Tracker</PanelTitle>
                        <Badge variant="info">{actionsQuery.data?.total ?? 0} actions</Badge>
                      </PanelHeader>
                      <PanelBody className="p-0">
                        {actionsQuery.isLoading && (
                          <div className="flex items-center justify-center gap-2 py-6">
                            <Spinner size="sm" />
                            <span className="text-sm text-gray-500 dark:text-gray-400">Loading…</span>
                          </div>
                        )}
                        {actionsQuery.data?.items.map((a, i) => (
                          <div
                            key={a.id}
                            className={cn(
                              'flex items-center gap-3 px-6 py-3',
                              i < (actionsQuery.data?.items.length ?? 0) - 1 && 'border-b border-gray-100 dark:border-dark-border/20',
                            )}
                          >
                            <div
                              className={cn(
                                'w-2 h-2 rounded-full shrink-0',
                                a.status === 'done'
                                  ? 'bg-emerald-500 dark:bg-emerald-400'
                                  : a.status === 'in_progress'
                                    ? 'bg-sky-500 dark:bg-sky-400'
                                    : a.status === 'open'
                                      ? 'bg-amber-500 dark:bg-amber-400'
                                      : 'bg-gray-400 dark:bg-gray-500',
                              )}
                            />
                            <div
                              className={cn(
                                'flex-1 text-sm',
                                a.status === 'done' ? 'text-gray-400 dark:text-gray-500 line-through' : 'text-charcoal dark:text-gray-100',
                              )}
                            >
                              {a.title}
                            </div>
                            {a.owner_email && (
                              <span className="text-xs text-gray-500 dark:text-gray-400 shrink-0">{a.owner_email}</span>
                            )}
                            {a.due_date && (
                              <span className="text-xs text-gray-500 dark:text-gray-400 font-mono shrink-0">{a.due_date}</span>
                            )}
                            <Badge variant={statusBadgeVariant(a.status)}>
                              {a.status.replace(/_/g, ' ').toUpperCase()}
                            </Badge>
                            <select
                              value={a.status}
                              onChange={(e) =>
                                updateActionMutation.mutate({ id: a.id, status: e.target.value })
                              }
                              className="bg-white dark:bg-dark-surface border border-gray-200 dark:border-dark-border/50 text-charcoal dark:text-gray-300 rounded px-1.5 py-1 text-xs cursor-pointer focus:outline-none focus:border-eliza-red/50"
                            >
                              <option value="open">Open</option>
                              <option value="in_progress">In Progress</option>
                              <option value="done">Done</option>
                            </select>
                          </div>
                        ))}
                        {actionsQuery.data?.items.length === 0 && (
                          <div className="px-6 py-6 text-center text-gray-500 dark:text-gray-400 text-sm">
                            No actions yet. Create one from Recommendations, Deal Urgency, or Market Pulse.
                          </div>
                        )}
                      </PanelBody>
                    </Panel>

                    {raw.trust?.panels && raw.trust.panels.length > 0 && (
                      <Panel>
                        <PanelHeader>
                          <PanelTitle>Data Freshness</PanelTitle>
                        </PanelHeader>
                        <PanelBody className="flex gap-5 flex-wrap">
                          {raw.trust.panels.map((p) => (
                            <div key={p.id} className="text-xs">
                              <span className="text-gray-500 dark:text-gray-400">{p.label}: </span>
                              <span className="font-mono text-xs text-sky-600 dark:text-sky-400">
                                {new Date(p.last_refreshed_at).toLocaleString()}
                              </span>
                            </div>
                          ))}
                        </PanelBody>
                      </Panel>
                    )}
                  </div>
                </TabsContent>
              </Tabs>
            </div>
          )}
        </PageBody>
      </Page>
    </Layout>
  );
}
